import { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Sheet } from 'components/ui/sheet'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import { AddressAutocomplete } from 'components/AddressAutocomplete'
import type { AddressResult } from 'components/AddressAutocomplete'
import { Trash2 } from 'lucide-react'
import {
	updateSite,
	updateSiteOwnerScoped,
	unlinkSiteFromEvaluation,
	hardDeleteSite,
} from 'slices/evaluationSiteSlice'
import { toast } from 'components/ui/toast-store'
import geocodeAddress from 'functions/geocodeAddress'
import { planSiteRegeocode, siteAddressSignature, siteAddressQuery } from 'utils/siteRegeocode'
import PopupCloseButton from 'components/PopupCloseButton'

interface SiteEditSheetProps {
	open: boolean
	onClose: () => void
	/**
	 * Eval context. When omitted (operator-wide Sites page), the sheet
	 *   - dispatches `updateSiteOwnerScoped` instead of the eval-scoped PUT
	 *   - re-labels the destructive button "Delete Permanently" and runs
	 *     `hardDeleteSite` (cascades scores) instead of an eval unlink.
	 */
	evalId?: string
	site: any
	/**
	 * Called after the site is permanently deleted (owner-scoped mode only).
	 * The site detail route uses this to navigate away — without it the entity
	 * is gone from the store and the route hangs on its loading state.
	 */
	onDeleted?: () => void
}

const SiteEditSheet = ({ open, onClose, evalId, site, onDeleted }: SiteEditSheetProps) => {
	const dispatch = useDispatch()
	const isEvalScoped = Boolean(evalId)

	const [name, setName] = useState('')
	const [address, setAddress] = useState('')
	const [city, setCity] = useState('')
	const [state, setState] = useState('')
	const [postalCode, setPostalCode] = useState('')
	const [propertyType, setPropertyType] = useState('')
	const [sqft, setSqft] = useState('')
	const [latitude, setLatitude] = useState('')
	const [longitude, setLongitude] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [deleteOpen, setDeleteOpen] = useState(false)
	const [deleting, setDeleting] = useState(false)
	// Why the last save or delete did not happen. Shown rather than swallowed.
	const [error, setError] = useState<string | null>(null)
	// Remounts the autocomplete each time the sheet targets a different site,
	// so it never shows the previous site's query.
	const [searchKey, setSearchKey] = useState(0)

	/**
	 * The address the current coordinates belong to.
	 *
	 * Starts as the stored address and moves forward whenever coordinates
	 * arrive from a source we trust (an autocomplete pick), so Save only
	 * re-geocodes an address that has genuinely drifted from its pin. Editing a
	 * name or a square footage costs no lookup and raises no prompt.
	 */
	const geocodedAddressRef = useRef('')
	/** Set when a save is paused waiting for the user to rule on the pin. */
	const [pinPrompt, setPinPrompt] = useState<string | null>(null)

	useEffect(() => {
		if (open && site) {
			setName(site.name || '')
			setAddress(site.address || '')
			setCity(site.city || '')
			setState(site.state || '')
			setPostalCode(site.postalCode || '')
			setPropertyType(site.propertyType || '')
			setSqft(site.sqft != null ? String(site.sqft) : '')
			setLatitude(site.latitude != null ? String(site.latitude) : '')
			setLongitude(site.longitude != null ? String(site.longitude) : '')
			setSubmitting(false)
			setDeleting(false)
			setDeleteOpen(false)
			setError(null)
			setPinPrompt(null)
			setSearchKey((k) => k + 1)
			geocodedAddressRef.current = siteAddressSignature(site)
		}
	}, [open, site])

	/**
	 * Fill the address block from a picked suggestion.
	 *
	 * Sets address and coordinates in one step, and moves the reference
	 * signature with them: a freshly picked address already matches its pin, so
	 * Save must not then geocode over the top of it.
	 */
	const handleAddressSelect = useCallback((result: AddressResult) => {
		const street = [result.address.house_number, result.address.road]
			.filter(Boolean)
			.join(' ')
			.trim()
		const picked = {
			address: street || address,
			city: result.address.city || city,
			state: result.address.state || state,
			postalCode: result.address.postcode || postalCode,
		}
		if (street) setAddress(street)
		if (result.address.city) setCity(result.address.city)
		if (result.address.state) setState(result.address.state)
		if (result.address.postcode) setPostalCode(result.address.postcode)
		setLatitude(String(result.latitude))
		setLongitude(String(result.longitude))
		geocodedAddressRef.current = siteAddressSignature(picked)
	}, [address, city, state, postalCode])

	/**
	 * Save, optionally moving the pin to follow the address.
	 *
	 * @param movePin When true, geocode the new address and use the result.
	 *   Passed explicitly rather than read from state because the confirm
	 *   dialog resolves after this callback was created, and a state read here
	 *   would see the value from before the user answered.
	 */
	const saveWith = useCallback(async (movePin: boolean) => {
		if (!name.trim() || !site) return
		setSubmitting(true)

		let lat = latitude
		let lng = longitude

		if (movePin) {
			const query = siteAddressQuery({ address, city, state, postalCode })
			try {
				const { lat: geoLat, lng: geoLng } = await geocodeAddress(query)
				lat = String(geoLat)
				lng = String(geoLng)
				setLatitude(lat)
				setLongitude(lng)
				geocodedAddressRef.current = siteAddressSignature({ address, city, state, postalCode })
			} catch (err) {
				// The edit still matters more than the pin, so the save goes
				// through either way. Say so, though, rather than leaving the
				// marker silently pointing at the old address.
				console.warn('[SiteEditSheet] Re-geocode failed', err)
				toast.warning(
					'The address was saved, but it could not be geocoded. The map pin still points at the previous location.',
				)
			}
		}

		const payload: any = {
			name: name.trim(),
			address: address.trim() || undefined,
			city: city.trim() || undefined,
			state: state.trim() || undefined,
			postalCode: postalCode.trim() || undefined,
			propertyType: propertyType.trim() || undefined,
			sqft: sqft ? Number(sqft) : undefined,
			latitude: lat ? Number(lat) : undefined,
			longitude: lng ? Number(lng) : undefined,
		}
		try {
			// Narrow with the optional itself — `isEvalScoped` is a derived
			// boolean and TS won't carry that narrowing back to `evalId`.
			const action = evalId
				? await (dispatch as any)(updateSite({ evalId, siteId: site._id, payload }))
				: await (dispatch as any)(updateSiteOwnerScoped({ siteId: site._id, payload }))

			// Same trap as the delete below: a rejected thunk resolves, so
			// closing unconditionally discards the user's edits and tells them
			// the save worked.
			if (action?.meta?.requestStatus === 'rejected') {
				setError(String(action.payload ?? 'The site could not be saved.'))
				return
			}
			onClose()
		} finally {
			setSubmitting(false)
			setPinPrompt(null)
		}
	}, [
		dispatch,
		evalId,
		site,
		name,
		address,
		city,
		state,
		postalCode,
		propertyType,
		sqft,
		latitude,
		longitude,
		onClose,
	])

	/**
	 * Decide what should happen to the pin, then save.
	 *
	 * The decision itself lives in `planSiteRegeocode`, where it is tested: a
	 * site whose coordinates were CAPTURED (a GPS fix, or a pin dragged on the
	 * map) holds a measurement, and correcting its address must not throw that
	 * away silently in favour of a geocoder's guess at a street centroid.
	 */
	const handleSave = useCallback(() => {
		if (!site) return
		const plan = planSiteRegeocode(site, geocodedAddressRef.current, {
			address,
			city,
			state,
			postalCode,
		})

		if (plan.action === 'confirm') {
			setPinPrompt(plan.prompt ?? 'Move the pin to the new address?')
			return
		}

		void saveWith(plan.action === 'geocode')
	}, [site, address, city, state, postalCode, saveWith])

	const handleDestructive = useCallback(async () => {
		if (!site) return
		setDeleting(true)
		setError(null)
		try {
			const action = evalId
				? await (dispatch as any)(unlinkSiteFromEvaluation({ evalId, siteId: site._id }))
				: await (dispatch as any)(hardDeleteSite(site._id))

			// createAsyncThunk RESOLVES on rejectWithValue, so awaiting it tells
			// you nothing. Closing here regardless is what made a refused delete
			// look like a button that does nothing at all.
			if (action?.meta?.requestStatus === 'rejected') {
				setError(String(action.payload ?? 'The site could not be deleted.'))
				return
			}

			setDeleteOpen(false)
			onClose()
			if (!evalId) onDeleted?.()
		} finally {
			setDeleting(false)
		}
	}, [dispatch, evalId, site, onClose, onDeleted])

	// Destructive button copy — different intent in each context.
	const destructiveLabel = isEvalScoped ? 'Remove Site' : 'Delete Permanently'
	const destructiveConfirmTitle = isEvalScoped ? 'Remove this site from this evaluation?' : 'Permanently delete this site?'
	const destructiveConfirmBody = isEvalScoped
		? `"${site?.name}" will be removed from this evaluation but kept in your Sites listing — you can re-add it to this or any other evaluation later.`
		: `"${site?.name}" will be permanently deleted along with every score recorded for it across every evaluation. This cannot be undone.`
	const destructiveConfirmCta = isEvalScoped ? 'Remove' : 'Delete Permanently'
	const destructiveCtaInFlight = isEvalScoped ? 'Removing...' : 'Deleting...'

	return (
		<>
			<Sheet open={open} onClose={onClose} side="right" className="!w-[400px] sm:!w-[540px]">
				<div className="flex flex-col h-full">
					<div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
						<div>
							<h2 className="text-lg font-semibold text-gray-900">Edit Site</h2>
							<p className="text-sm text-gray-500 mt-0.5">
								{isEvalScoped
									? 'Update site details, or remove it from this evaluation.'
									: 'Update site details, or permanently delete it across every evaluation.'}
							</p>
						</div>
						<PopupCloseButton onClose={onClose} />
					</div>

					<div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
						<div className="grid grid-cols-12 gap-3">
							<div className="col-span-7">
								<Label htmlFor="editSiteName" className="mb-1.5 block">Site Name *</Label>
								<Input
									id="editSiteName"
									value={name}
									onChange={(e) => setName(e.target.value)}
								/>
							</div>
							<div className="col-span-5">
								<Label htmlFor="editPropertyType" className="mb-1.5 block">Property Type</Label>
								<Input
									id="editPropertyType"
									value={propertyType}
									onChange={(e) => setPropertyType(e.target.value)}
									placeholder="e.g. Retail, Office"
								/>
							</div>

							<div className="col-span-12">
								<Label className="mb-1.5 block">Find Address</Label>
								<AddressAutocomplete
									key={searchKey}
									placeholder="Search an address to fill the fields below..."
									onSelect={handleAddressSelect}
								/>
								<p className="mt-1 text-xs italic text-[#0080fe]">
									Picking a result fills the address and sets the coordinates. Editing the
									fields by hand moves the pin to match, and asks first if this site&rsquo;s
									position was captured on-site.
								</p>
							</div>

							<div className="col-span-12">
								<Label htmlFor="editAddr" className="mb-1.5 block">Address</Label>
								<Input
									id="editAddr"
									value={address}
									onChange={(e) => setAddress(e.target.value)}
								/>
							</div>

							<div className="col-span-5">
								<Label htmlFor="editCity" className="mb-1.5 block">City</Label>
								<Input id="editCity" value={city} onChange={(e) => setCity(e.target.value)} />
							</div>
							<div className="col-span-3">
								<Label htmlFor="editState" className="mb-1.5 block">State/Prov</Label>
								<Input id="editState" value={state} onChange={(e) => setState(e.target.value)} />
							</div>
							<div className="col-span-4">
								<Label htmlFor="editPostalCode" className="mb-1.5 block">Postal Code</Label>
								<Input
									id="editPostalCode"
									value={postalCode}
									onChange={(e) => setPostalCode(e.target.value)}
								/>
							</div>

							<div className="col-span-4">
								<Label htmlFor="editSqft" className="mb-1.5 block">SQFT</Label>
								<Input
									id="editSqft"
									type="number"
									value={sqft}
									onChange={(e) => setSqft(e.target.value)}
								/>
							</div>
							<div className="col-span-4">
								<Label htmlFor="editLat" className="mb-1.5 block">Latitude</Label>
								<Input
									id="editLat"
									value={latitude}
									onChange={(e) => setLatitude(e.target.value)}
								/>
							</div>
							<div className="col-span-4">
								<Label htmlFor="editLng" className="mb-1.5 block">Longitude</Label>
								<Input
									id="editLng"
									value={longitude}
									onChange={(e) => setLongitude(e.target.value)}
								/>
							</div>
						</div>
					</div>

					{/* Sits above the footer so it is next to the button that failed,
					    not at the top of a form the user has scrolled past. */}
					{error && !deleteOpen && (
						<div
							role="alert"
							className="border-t border-red-100 bg-red-50 px-6 py-3 text-sm text-red-700"
						>
							{error}
						</div>
					)}

					<div className="border-t border-gray-100 px-6 py-4 flex justify-between items-center">
						<Button
							variant="danger"
							isOutline
							onClick={() => setDeleteOpen(true)}
							disabled={submitting || deleting}
						>
							<Trash2 className="w-4 h-4" />
							{destructiveLabel}
						</Button>
						<div className="flex gap-2">
							<Button variant="ghost" onClick={onClose} disabled={submitting}>
								Cancel
							</Button>
							<Button
								variant="primary"
								onClick={handleSave}
								disabled={!name.trim() || submitting}
							>
								{submitting ? 'Saving...' : 'Save'}
							</Button>
						</div>
					</div>
				</div>
			</Sheet>

			{/*
				The captured-position prompt.

				Both choices save the edit; they differ only in what happens to the
				pin, and KEEPING it is the default action. Nothing here is
				destructive to the address the user just typed, so neither button
				is styled as a danger.
			*/}
			<Dialog open={Boolean(pinPrompt)} onClose={() => setPinPrompt(null)}>
				<DialogContent className="max-w-sm">
					<DialogHeader>
						<DialogTitle>Move the map pin?</DialogTitle>
						<DialogDescription>{pinPrompt}</DialogDescription>
					</DialogHeader>
					<p className="mt-3 text-sm text-gray-600">
						Its coordinates were recorded on the ground. An address lookup returns
						roughly where the street is, which is usually less precise.
					</p>
					<div className="flex justify-end gap-2 pt-4">
						<Button variant="ghost" onClick={() => void saveWith(true)} disabled={submitting}>
							{submitting ? 'Saving...' : 'Move the pin'}
						</Button>
						<Button variant="primary" onClick={() => void saveWith(false)} disabled={submitting}>
							{submitting ? 'Saving...' : 'Keep the captured position'}
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
				<DialogContent className="max-w-sm">
					<DialogHeader>
						<DialogTitle>{destructiveConfirmTitle}</DialogTitle>
						<DialogDescription>{destructiveConfirmBody}</DialogDescription>
					</DialogHeader>
					{/* Padded body. DialogContent carries no padding of its own, so
					    without this wrapper the error and the buttons sit flush
					    against the bottom-right corner of the dialog. */}
					<div className="px-5 py-4">
						{/* The dialog stays open on failure, so the reason belongs here —
						    closing it and reporting elsewhere is how this looked silent. */}
						{error && (
							<p role="alert" className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
								{error}
							</p>
						)}
						<div className="flex justify-end gap-2">
							<Button variant="ghost" onClick={() => setDeleteOpen(false)} disabled={deleting}>
								Cancel
							</Button>
							<Button variant="danger" onClick={handleDestructive} disabled={deleting}>
								{deleting ? destructiveCtaInFlight : destructiveConfirmCta}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</>
	)
}

export default SiteEditSheet
